import { ReactNode, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useLocation } from "react-router";
import { ScrollView, View, XStack, YStack, useMedia } from "tamagui";

import { MobileTopBar, Sidebar } from "./Sidebar";

interface LayoutProps {
  noPadding?: boolean;
  children: ReactNode;
}

const Layout = ({ noPadding, children }: LayoutProps) => {
  const { md, xs } = useMedia();
  // key, not pathname: tapping the page you're already on still closes it.
  const { key } = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const pageRef = useRef<HTMLDivElement>(null);
  const isDrawerOpen = md && isMenuOpen;

  // Picking a page closes the drawer; so does widening past `md`.
  useEffect(() => setIsMenuOpen(false), [key, md]);

  // The open drawer is modal: the page behind it is inert, and closing hands
  // focus back to the menu button. A layout effect, so the opener is read
  // before the drawer focuses its close button, and inert is gone before the
  // opener is focused again.
  useLayoutEffect(() => {
    const page = pageRef.current;
    if (!isDrawerOpen || !page) return undefined;
    const opener = document.activeElement as HTMLElement | null;
    page.setAttribute("inert", "");
    return () => {
      page.removeAttribute("inert");
      opener?.focus();
    };
  }, [isDrawerOpen]);

  return (
    // dvh, not vh: on a phone vh includes the space under the browser's
    // address bar, which pushed the page bottom off screen.
    <XStack flex={1} height="100dvh">
      <Sidebar isOpen={isMenuOpen} onOpenChange={setIsMenuOpen} />
      <YStack ref={pageRef} flex={1} minWidth={0}>
        {md && <MobileTopBar onMenuPress={() => setIsMenuOpen(true)} />}
        <ScrollView
          flex={1}
          backgroundColor="$gray1"
          showsVerticalScrollIndicator={false}
          contentContainerStyle={{
            flexGrow: 1,
            ...(!noPadding && {
              paddingVertical: xs ? "$4" : md ? "$5" : "$8",
              paddingHorizontal: xs ? "$3" : md ? "$4" : "$5",
            }),
          }}
        >
          <View width="100%" maxWidth={1800} alignSelf="center" flexGrow={1}>
            {children}
          </View>
        </ScrollView>
      </YStack>
    </XStack>
  );
};

export default Layout;
